import { BaseError } from "make-error";
import { downloadResponse } from "../../utils/download";
import { sleep } from "../../utils/time";

export class ForbiddenError extends BaseError {}

export class ServiceUnavailableError extends BaseError {}

/** Base client for Quartz APIs. */
export class BaseClient {
  constructor(
    private readonly url: string,
    private readonly authorizationHeader: () => Promise<string | null> = async () => null
  ) {}

  // Helper to build request headers.
  private async createHeaders(contentType?: string): Promise<{ [key: string]: string }> {
    const headers: { [name: string]: string } = {};
    if (contentType) {
      headers["Content-Type"] = contentType;
    }
    const auth = await this.authorizationHeader();
    if (auth) {
      headers["Authorization"] = auth;
    }
    return headers;
  }

  // Helper to handle JSON responses.
  private async handleJsonResponse(response: Response): Promise<any> {
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    const body = await response.json().catch(() => ({}));
    if (response.status === 200) {
      return body;
    } else {
      throw new Error(body.detail || `Unexpected error ${response.status}`);
    }
  }

  // Helper to handle blob responses.
  private async handleBlobResponse(response: Response): Promise<void> {
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    if (response.status === 404) {
      throw new Error("Resource not found");
    }
    if (response.status === 200) {
      return await downloadResponse(response, "export.zip");
    }
    const body = await response.json().catch(() => ({}));
    if (response.status === 500) {
      throw new Error("Unexpected technical error");
    }
    throw new Error(body.detail || `Unexpected status ${response.status}`);
  }

  // GET a binary resource with retry logic for transient errors.
  protected async httpGetBlob(path: string, controller?: AbortController): Promise<void> {
    let response: Response;
    while (true) {
      try {
        const headers = await this.createHeaders();
        response = await fetch(`${this.url}${path}`, {
          headers,
          signal: controller?.signal,
          mode: "cors",
        });
        break;
      } catch (err: any) {
        if (err.name === "AbortError") throw err;
        console.error(err);
        await sleep(1000, controller);
      }
    }
    await this.handleBlobResponse(response);
  }

  // POST a binary resource.
  protected async httpPostBlob(
    path: string,
    body: any,
    controller?: AbortController
  ): Promise<void> {
    const headers = await this.createHeaders("application/json");
    const response = await fetch(`${this.url}${path}`, {
      method: "POST",
      mode: "cors",
      headers,
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    await this.handleBlobResponse(response);
  }

  // GET a JSON resource.
  protected async httpGet(path: string, controller?: AbortController): Promise<any> {
    const headers = await this.createHeaders();
    const response = await fetch(`${this.url}${path}`, {
      headers,
      signal: controller?.signal,
      mode: "cors",
    });
    return this.handleJsonResponse(response);
  }

  // POST a JSON resource.
  protected async httpPost(path: string, body: any, controller?: AbortController): Promise<any> {
    const headers = await this.createHeaders("application/json");
    const response = await fetch(`${this.url}${path}`, {
      method: "POST",
      mode: "cors",
      headers,
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    return this.handleJsonResponse(response);
  }

  protected async httpPostFormData(
    path: string,
    body: FormData,
    controller?: AbortController
  ): Promise<any> {
    // Build headers without specifying a Content-Type header so that the browser can set it properly.
    const auth = await this.authorizationHeader();
    const headers: { [name: string]: string } = {};
    if (auth) {
      headers["Authorization"] = auth;
    }

    const response = await fetch(`${this.url}${path}`, {
      method: "POST",
      mode: "cors",
      headers,
      body,
      signal: controller?.signal,
    });
    return this.handleJsonResponse(response);
  }

  // DELETE a JSON resource.
  protected async httpDelete(path: string, body: any, controller?: AbortController): Promise<any> {
    const headers = await this.createHeaders("application/json");
    const response = await fetch(`${this.url}${path}`, {
      method: "DELETE",
      mode: "cors",
      headers,
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    return this.handleJsonResponse(response);
  }

  // PUT a JSON resource.
  protected async httpPut(path: string, body: any, controller?: AbortController): Promise<any> {
    const headers = await this.createHeaders("application/json");
    const response = await fetch(`${this.url}${path}`, {
      method: "PUT",
      mode: "cors",
      headers,
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    return this.handleJsonResponse(response);
  }

  // PATCH a JSON resource.
  protected async httpPatch(path: string, body: any, controller?: AbortController): Promise<any> {
    const headers = await this.createHeaders("application/json");
    const response = await fetch(`${this.url}${path}`, {
      method: "PATCH",
      mode: "cors",
      headers,
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    return this.handleJsonResponse(response);
  }
}
